import type { AnyExtension, NodeViewRenderer } from "@tiptap/core";
import type { Node as ProseNode } from "@tiptap/pm/model";
import { TextSelection } from "@tiptap/pm/state";
import type { EditorView } from "@tiptap/pm/view";
import { extensions } from "../chapter_markdown";
import { BOX_KINDS, classesOf, isBox, withKind } from "./boxes";
import type { EditorContext } from "./context";
import { button, element } from "./dom";
import { type DiceTest, readTest, shortOf } from "./dice_test";
import { diceForm, kindPicker } from "./panels";
import type { Preview } from "./previews";
import { sanitizePreview } from "./sanitize";
import { tableView, TableStripes } from "./table_view";

export const ATOMS = new Set(["odoTag", "odoBox", "odoImage", "rawHtmlBlock", "rawHtmlInline"]);

const LINK = "text-link underline hover:no-underline";
const insideControl = (event: Event) => event.target instanceof Element && event.target.closest("textarea, input, select, button") !== null;

// Replaces the node a view draws, attributes and all, with the editor in charge of history.
function replaceNode(view: EditorView, getPos: () => number | undefined, node: ProseNode, next: ProseNode | null, attrs?: Record<string, unknown>) {
	const pos = getPos();
	if (pos === undefined) return;
	view.dispatch(next ? view.state.tr.replaceWith(pos, pos + node.nodeSize, next) : view.state.tr.setNodeMarkup(pos, undefined, { ...node.attrs, ...attrs }));
}

// The reader's HTML for a fragment arrives as a paragraph; an inline piece lives inside one already.
const unwrap = (html: string) => /^\s*<p[^>]*>([\s\S]*)<\/p>\s*$/.exec(html)?.[1] ?? html;

function paintAtom(body: HTMLElement, markdown: string, preview: Preview | undefined, inline: boolean, t: EditorContext["t"]) {
	body.replaceChildren();
	body.classList.toggle("odo-chapter-atom--error", preview?.error !== undefined);
	if (preview?.html !== undefined) {
		const html = element(inline ? "span" : "div", "odo-markdown");
		html.setAttribute("inert", "");
		html.append(sanitizePreview(inline ? unwrap(preview.html) : preview.html));
		body.append(html);
		return;
	}
	if (preview?.error !== undefined) {
		body.title = preview.error;
		if (!inline) body.append(element("p", "mb-1 text-sm font-bold", `${t("preview_error")} ${preview.error}`));
	}
	body.append(element("code", "font-mono text-sm break-all", markdown));
}

interface AtomOptions {
	inline: boolean;
	footer?: (api: { node: () => ProseNode; replace: (attrs: Record<string, unknown>) => void }) => HTMLElement;
}

// Draws `fragment()` as the server renders it for the reader, once `dom` is near the viewport; until then,
// and when the request fails, `paint` gets no preview and shows the raw notation. `refresh` redraws after the node changed.
function livePreview(ctx: EditorContext, dom: HTMLElement, fragment: () => string, paint: (markdown: string, preview?: Preview) => void) {
	let alive = true;
	let drawn: { markdown: string; preview?: Preview } | undefined;
	let near = false;
	let requested = "";
	const draw = () => {
		const markdown = fragment();
		const shown = ctx.previews.cached(markdown);
		if (drawn?.markdown !== markdown || drawn.preview !== shown) {
			drawn = { markdown, preview: shown };
			paint(markdown, shown);
		}
		if (shown || !near || requested === markdown) return;
		requested = markdown;
		void ctx.previews.request(markdown).then((result) => {
			if (result && alive && fragment() === markdown) draw();
		});
	};
	draw();
	const unwatch = ctx.near(dom, () => {
		near = true;
		draw();
	});
	return {
		refresh: () => {
			near = true;
			draw();
		},
		destroy: () => {
			alive = false;
			unwatch();
		},
	};
}

// Notation the editor does not edit in place: drawn by the server as the reader sees it, and the raw
// notation while that is on its way or when it fails. Selecting it offers Remover (the controller's bar).
function atomView(ctx: EditorContext, { inline, footer }: AtomOptions): NodeViewRenderer {
	return ({ node: first, editor, getPos }) => {
		let node = first;
		const dom = element(inline ? "span" : "div", inline ? "odo-chapter-atom odo-chapter-atom--inline" : "odo-chapter-atom");
		dom.contentEditable = "false";
		const body = element(inline ? "span" : "div", "");
		dom.append(body);
		const fragment = () => shortOf(ctx.manager, inline ? { type: "paragraph", content: [node.toJSON()] } : node.toJSON()).trim();
		dom.addEventListener("click", (event) => {
			if (event.target instanceof Element && event.target.closest("a")) event.preventDefault();
		});
		if (footer) dom.append(footer({ node: () => node, replace: (attrs) => replaceNode(editor.view, getPos, node, null, attrs) }));
		const live = livePreview(ctx, dom, fragment, (markdown, shown) => paintAtom(body, markdown, shown, inline, ctx.t));
		return {
			dom,
			update: (next) => {
				if (next.type !== node.type) return false;
				node = next;
				live.refresh();
				return true;
			},
			stopEvent: insideControl,
			ignoreMutation: () => true,
			destroy: live.destroy,
		};
	};
}

// Raw HTML is edited as code, this block alone; it reaches the node's `raw`, nothing else in the chapter.
const rawFooter =
	(ctx: EditorContext): AtomOptions["footer"] =>
	({ node, replace }) => {
		const footer = element("div", "mt-2 flex items-start gap-3 rounded border border-dashed border-ink-faint p-2.5 text-sm text-ink-muted");
		const note = element("span", "", ctx.t("raw_note"));
		const edit = button(
			"ml-auto shrink-0 rounded border border-line bg-surface-raised px-2.5 py-0.5 text-ink hover:bg-surface-sunken",
			ctx.t("raw_edit"),
			() => {
				area.value = String(node().attrs.raw);
				form.classList.remove("hidden");
				note.classList.add("hidden");
				edit.classList.add("hidden");
				area.focus();
			},
		);
		const area = element("textarea", "odo-form__textarea w-full font-mono text-sm text-ink");
		area.setAttribute("aria-label", ctx.t("raw_area"));
		area.rows = 8;
		area.spellcheck = false;
		const close = () => {
			form.classList.add("hidden");
			note.classList.remove("hidden");
			edit.classList.remove("hidden");
		};
		const form = element("div", "hidden w-full space-y-2");
		const actions = element("div", "flex gap-2");
		actions.append(
			button("odo-button__primary", ctx.t("apply"), () => {
				const raw = area.value.replace(/\n+$/, "");
				if (!raw.trim()) return;
				replace({ raw });
				close();
			}),
			button("odo-button__alt", ctx.t("cancel"), close),
		);
		form.append(area, actions);
		footer.append(note, edit, form);
		return footer;
	};

const testOf = (ctx: EditorContext, node: ProseNode) =>
	classesOf(node.attrs.ial) === "roll" && isBox(node.attrs.ial) ? readTest(shortOf(ctx.manager, node.toJSON())) : null;

function testCard(ctx: EditorContext, node: ProseNode, edit: () => void) {
	const card = element("div", "odo-chapter-test odo-chapter-atom");
	const bar = element("div", "odo-chapter-bar");
	bar.append(element("span", "text-ink-muted", ctx.t("test_label")), button(LINK, ctx.t("edit"), edit));
	card.append(bar);
	const body = element("div", "");
	card.append(body);
	const markdown = () => shortOf(ctx.manager, node.toJSON()).trim();
	return { dom: card, destroy: livePreview(ctx, card, markdown, (text, shown) => paintAtom(body, text, shown, false, ctx.t)).destroy };
}

// A blockquote is a box (a framed block with its kind in a menu), a dice test (a card edited in a form),
// or a plain quote. Which one is decided from what the serializer would write, so the card shows
// exactly the tests that come out as `:::teste`.
function quoteView(ctx: EditorContext): NodeViewRenderer {
	return ({ node: first, editor, getPos }) => {
		let node = first;
		const kindOf = (n: ProseNode) => (testOf(ctx, n) ? "test" : isBox(n.attrs.ial) ? "box" : "quote");
		const kind = kindOf(node);
		const view = editor.view;
		if (kind === "test") {
			const edit = () =>
				ctx.openPanel(ctx.t("panel_test"), (close) =>
					diceForm(ctx, testOf(ctx, node) as DiceTest, ctx.t("apply"), (json) => {
						replaceNode(view, getPos, node, editor.schema.nodeFromJSON(json));
						close();
					}),
				);
			const card = testCard(ctx, node, edit);
			return {
				dom: card.dom,
				destroy: card.destroy,
				update: (next) => next.eq(node),
				stopEvent: insideControl,
				ignoreMutation: () => true,
			};
		}
		const dom = element("div", "odo-chapter-box");
		const quote = element("blockquote", "");
		const bar = element("div", "odo-chapter-bar");
		bar.contentEditable = "false";
		const label = element("span", "text-ink-muted", ctx.t("box_label"));
		const change = button(LINK, "", () =>
			ctx.openPanel(ctx.t("panel_box"), (close) =>
				kindPicker(
					ctx,
					(cls) => {
						replaceNode(view, getPos, node, null, { ial: withKind(node.attrs.ial, cls) });
						close();
					},
					classesOf(node.attrs.ial),
				),
			),
		);
		bar.append(label, change);
		const paint = () => {
			const classes = classesOf(node.attrs.ial);
			quote.className = classes;
			const names = classes.split(" ").map((cls) => BOX_KINDS.find((kind) => kind.cls === cls)?.label ?? "");
			change.textContent = `${names.map((name, i) => (name ? ctx.t(name) : classes.split(" ")[i])).join(" + ")} ▾`;
			bar.classList.toggle("hidden", kind !== "box");
		};
		paint();
		dom.append(bar, quote);
		return {
			dom,
			contentDOM: quote,
			update: (next) => {
				if (next.type !== node.type || kindOf(next) !== kind) return false;
				node = next;
				paint();
				return true;
			},
			stopEvent: (event) => bar.contains(event.target as Node),
			ignoreMutation: (mutation) => !quote.contains(mutation.target) || (mutation.type === "attributes" && mutation.target === quote),
		};
	};
}

// Left to ProseMirror, Backspace after a dice test would join the next paragraph into the test's hidden lines.
// The card goes as an atom goes.
export function guardTests(ctx: EditorContext, view: EditorView, event: KeyboardEvent): boolean {
	const back = event.key === "Backspace";
	const { selection, doc } = view.state;
	// A cursor between blocks sits at depth zero, where there is no parent textblock to be at the edge of.
	if ((!back && event.key !== "Delete") || !(selection instanceof TextSelection) || !selection.empty || selection.$from.depth === 0) return false;
	const { $from } = selection;
	if (back ? $from.parentOffset !== 0 : $from.parentOffset !== $from.parent.content.size) return false;
	const sibling = back ? doc.resolve($from.before()).nodeBefore : doc.resolve($from.after()).nodeAfter;
	if (!sibling || !testOf(ctx, sibling)) return false;
	const at = back ? $from.before() - sibling.nodeSize : $from.after();
	view.dispatch(view.state.tr.delete(at, at + sibling.nodeSize));
	return true;
}

const VIEWS: Record<string, (ctx: EditorContext) => NodeViewRenderer> = {
	odoTag: (ctx) => atomView(ctx, { inline: true }),
	rawHtmlInline: (ctx) => atomView(ctx, { inline: true }),
	odoImage: (ctx) => atomView(ctx, { inline: true }),
	odoBox: (ctx) => atomView(ctx, { inline: false }),
	rawHtmlBlock: (ctx) => atomView(ctx, { inline: false, footer: rawFooter(ctx) }),
	blockquote: quoteView,
	table: tableView,
};

// The serializer's schema with undo and cursors on, and the views the chapter editor adds to it.
export function editorExtensions(ctx: EditorContext) {
	return [...extensions, TableStripes].map((ext) => {
		if (ext.name === "starterKit") return ext.configure({ undoRedo: {}, dropcursor: {}, gapcursor: {} } as never);
		const view = VIEWS[ext.name];
		return view ? (ext as AnyExtension).extend({ addNodeView: () => view(ctx) }) : ext;
	});
}
